import { lazy } from 'react'
import { Outlet, redirect } from 'react-router'

import {
  ErrorPage,
  ErrorPageNotFound,
  RouteErrorPage,
} from '../components/ErrorPage'
import { RouteRedirectHome } from '../components/Route'
import { APP_STATUS_ERROR, EMPTY_STRING } from '../constants/common'
import {
  PROFILE_ROUTE_NAMES,
  ROUTE_PARAM_BUILDING_GROUP_ID,
  ROUTE_PARAM_BUILDING_ID,
  ROUTE_PARAM_CHATROOM_UUID,
  ROUTE_PARAM_INHABITANT_ID,
  ROUTE_PARAM_MAIL_UUID,
  ROUTE_PARAM_OUTBOUND_UUID,
  ROUTE_PATH_LIST,
  ROUTE_PATH_LIST_LEGACY,
  ROUTE_PATH_LIST_RENTAL_PORTAL,
  ROUTE_UI_SECTION_NAME,
  ROUTE_UI_SECTION_NAME_RENTAL_PORTAL,
} from '../constants/route'
import { withRentalPortalMockSections } from '../utils/rentalPortalMock'
import {
  createPostProfileLoader,
  getAvailableSectionWithRedirectToWebappLoader,
  getBuildingGroupLegacyRedirectLoader,
  getBuildingLegacyRedirectLoader,
  getChatLegacyRedirectLoader,
  getExternalContactProfileGuardLoader,
  getIntegerParamGuardLoader,
  getPermissionGuardLoader,
  getPermissionProtectedRouteLoader,
  getProfileLegacyRedirectLoader,
  getProviderProfileGuardLoader,
  getUuidParamGuardLoader,
} from '../utils/route'

import { getSearchResultsRoutes } from './searchResultsRoutes'

const AboutUs = lazy(() =>
  import('../components/AboutUs').then(m => ({
    default: m.AboutUs,
  }))
)

const DashboardBirdview = lazy(() =>
  import('../components/DashboardBirdview').then(m => ({
    default: m.DashboardBirdview,
  }))
)

const EmailMessageProfilePage = lazy(() =>
  import('../components/Email/EmailMessageProfilePage').then(m => ({
    default: m.EmailMessageProfilePage,
  }))
)
const EmailThreadProfilePage = lazy(() =>
  import('../components/Email/EmailThreadProfilePage').then(m => ({
    default: m.EmailThreadProfilePage,
  }))
)
const Emails = lazy(() =>
  import('../components/Emails').then(m => ({ default: m.Emails }))
)

const RequestProfile = lazy(() =>
  import('../components/RequestProfile').then(m => ({
    default: m.RequestProfile,
  }))
)

const BudgetPlanProfile = lazy(() =>
  import('../components/BudgetPlanProfile').then(m => ({
    default: m.BudgetPlanProfile,
  }))
)

const Requests = lazy(() =>
  import('../components/Requests').then(m => ({ default: m.Requests }))
)
const ConnectedRequestProfile = lazy(() =>
  import('../components/IdwellToIdwell/ConnectedRequestProfile').then(m => ({
    default: m.ConnectedRequestProfile,
  }))
)
const IdwellToIdwells = lazy(() =>
  import('../components/IdwellToIdwells').then(m => ({
    default: m.IdwellToIdwells,
  }))
)
const Inhabitants = lazy(() =>
  import('../components/Inhabitants').then(m => ({ default: m.Inhabitants }))
)
const UserProfilePage = lazy(() =>
  import('../components/UserProfilePage').then(m => ({
    default: m.UserProfilePage,
  }))
)
const ExternalContactProfilePage = lazy(() =>
  import('../components/ExternalContact/ExternalContactProfilePage').then(
    m => ({
      default: m.ExternalContactProfilePage,
    })
  )
)
const Flats = lazy(() =>
  import('../components/Flats').then(m => ({ default: m.Flats }))
)
const FlatProfile = lazy(() =>
  import('../components/Flat/FlatProfile').then(m => ({
    default: m.FlatProfile,
  }))
)
const Buildings = lazy(() =>
  import('../components/Buildings').then(m => ({ default: m.Buildings }))
)
const BuildingProfile = lazy(() =>
  import('../components/BuildingProfile').then(m => ({
    default: m.BuildingProfile,
  }))
)
const BuildingGroups = lazy(() =>
  import('../components/BuildingGroups').then(m => ({
    default: m.BuildingGroups,
  }))
)
const BuildingGroupProfile = lazy(() =>
  import('../components/BuildingGroupProfile').then(m => ({
    default: m.BuildingGroupProfile,
  }))
)
const Tasks = lazy(() =>
  import('../components/Tasks').then(m => ({ default: m.Tasks }))
)
const CalendarPage = lazy(() =>
  import('../components/Calendar').then(m => ({ default: m.CalendarPage }))
)
const Chats = lazy(() =>
  import('../components/Chats').then(m => ({ default: m.Chats }))
)
const ChatProfile = lazy(() =>
  import('../components/Chat/ChatProfile').then(m => ({
    default: m.ChatProfile,
  }))
)
const Documents = lazy(() =>
  import('../components/Documents').then(m => ({ default: m.Documents }))
)
const DocumentsIndexPage = lazy(() =>
  import('../components/Documents').then(m => ({
    default: m.DocumentsIndexPage,
  }))
)
const Providers = lazy(() =>
  import('../components/Providers').then(m => ({ default: m.Providers }))
)
const ProviderProfilePage = lazy(() =>
  import('../components/Provider/ProviderProfilePage').then(m => ({
    default: m.ProviderProfilePage,
  }))
)
const RentalPortalArchive = lazy(() =>
  import('../components/RentalPortal').then(m => ({
    default: m.RentalPortalArchive,
  }))
)
const RentalPortalListingProfile = lazy(() =>
  import('../components/RentalPortal').then(m => ({
    default: m.RentalPortalListingProfile,
  }))
)
const RentalPortalListingProfilePreview = lazy(() =>
  import('../components/RentalPortal').then(m => ({
    default: m.RentalPortalListingProfilePreview,
  }))
)
const RentalPortalApplicantProfile = lazy(() =>
  import('../components/RentalPortal').then(m => ({
    default: m.RentalPortalApplicantProfile,
  }))
)
const RentalPortalApplicants = lazy(() =>
  import('../components/RentalPortal').then(m => ({
    default: m.RentalPortalApplicants,
  }))
)
const RentalPortalListings = lazy(() =>
  import('../components/RentalPortal').then(m => ({
    default: m.RentalPortalListings,
  }))
)
const RentalPortalOverview = lazy(() =>
  import('../components/RentalPortal').then(m => ({
    default: m.RentalPortalOverview,
  }))
)
const RentalPortalIndexRedirect = lazy(() =>
  import('../components/RentalPortal').then(m => ({
    default: m.RentalPortalIndexRedirect,
  }))
)
const RentalPortalSubsectionPage = lazy(() =>
  import('../components/RentalPortal').then(m => ({
    default: m.RentalPortalSubsectionPage,
  }))
)
const MatchSettings = lazy(() =>
  import('../components/RentalPortal/RentalPortalMatch/MatchSettings').then(
    m => ({ default: m.MatchSettings })
  )
)
const MatchCriterionPage = lazy(() =>
  import('../components/RentalPortal/RentalPortalMatch/MatchSettings/MatchCriterionPage').then(
    m => ({ default: m.MatchCriterionPage })
  )
)
const RentalPortalIntegrations = lazy(() =>
  import('../components/RentalPortal/RentalPortalIntegrations').then(m => ({
    default: m.RentalPortalIntegrations,
  }))
)

const IntegrationsImportLog = lazy(() =>
  import('../components/RentalPortal/RentalPortalIntegrations/IntegrationsImportLog').then(
    m => ({ default: m.IntegrationsImportLog })
  )
)

const IntegrationsImportLogRun = lazy(() =>
  import('../components/RentalPortal/RentalPortalIntegrations/IntegrationsImportLog/IntegrationsImportLogRun').then(
    m => ({ default: m.IntegrationsImportLogRun })
  )
)

const RentalPortalRightsRoles = lazy(() =>
  import('../components/RentalPortal/RentalPortalRightsRoles').then(m => ({
    default: m.RentalPortalRightsRoles,
  }))
)

const RentalPortalRulesPolicy = lazy(() =>
  import('../components/RentalPortal/RentalPortalRulesPolicy').then(m => ({
    default: m.RentalPortalRulesPolicy,
  }))
)
const DocFlows = lazy(() =>
  import('../components/DocFlows').then(m => ({ default: m.DocFlows }))
)
const DocFlow = lazy(() =>
  import('../components/DocFlow').then(m => ({ default: m.DocFlow }))
)
const FAQs = lazy(() =>
  import('../components/FAQs').then(m => ({ default: m.FAQs }))
)
const Posts = lazy(() =>
  import('../components/Posts').then(m => ({ default: m.Posts }))
)
const NewsboardPostProfile = lazy(() =>
  import('../components/Newsboard').then(m => ({
    default: m.NewsboardPostProfile,
  }))
)

function getGuardedChildren({
  availableSections,
  hasCalendar,
  isExternalContactProfileNGAvailable,
  isProviderProfileNGAvailable,
  isUserProfileNGAvailable,
  sections,
}) {
  const rentalPortalSections = withRentalPortalMockSections(sections)

  return [
    ...getSearchResultsRoutes({ availableSections, sections }),
    {
      children: [
        { element: <Emails />, index: true },
        {
          element: <EmailMessageProfilePage />,
          loader: getUuidParamGuardLoader({
            loader: getPermissionGuardLoader({
              sectionName: ROUTE_UI_SECTION_NAME.mailThread,
              sections,
            }),
            paramName: ROUTE_PARAM_MAIL_UUID,
          }),
          path: ROUTE_PATH_LIST.mailMessage,
        },
        {
          element: <EmailThreadProfilePage />,
          loader: getUuidParamGuardLoader({
            loader: getPermissionGuardLoader({
              sectionName: ROUTE_UI_SECTION_NAME.mailThread,
              sections,
            }),
            paramName: ROUTE_PARAM_MAIL_UUID,
          }),
          path: ROUTE_PATH_LIST.mailThread,
        },
      ],
      loader: getPermissionGuardLoader({
        sectionName: ROUTE_UI_SECTION_NAME.mails,
        sections,
      }),
      path: ROUTE_PATH_LIST.mails,
    },
    {
      children: [
        {
          element: <Requests />,
          index: true,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.serviceRequests,
            sections,
          }),
        },
        {
          element: <RequestProfile />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.serviceRequest,
            sections,
          }),
          path: ROUTE_PATH_LIST.serviceRequest,
        },
      ],

      path: ROUTE_PATH_LIST.serviceRequests,
    },
    {
      element: <BudgetPlanProfile />,
      loader: getPermissionGuardLoader({
        sectionName: ROUTE_UI_SECTION_NAME.budgetPlan,
        sections,
      }),
      path: ROUTE_PATH_LIST.budgetPlanProfile,
    },
    {
      element: <NewsboardPostProfile />,
      loader: createPostProfileLoader({ availableSections, sections }),
      path: ROUTE_PATH_LIST.post,
    },
    {
      children: [
        {
          element: <IdwellToIdwells />,
          index: true,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.idwellToIdwell,
            sections,
          }),
        },
        {
          element: <ConnectedRequestProfile />,
          loader: getUuidParamGuardLoader({
            loader: getPermissionGuardLoader({
              sectionName: ROUTE_UI_SECTION_NAME.idwellToIdwellRequest,
              sections,
            }),
            paramName: ROUTE_PARAM_OUTBOUND_UUID,
          }),
          path: ROUTE_PATH_LIST.connectedRequestProfile,
        },
      ],
      path: ROUTE_PATH_LIST.idwellToIdwell,
    },
    {
      children: [
        {
          element: <Inhabitants />,
          index: true,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.inhabitants,
            sections,
          }),
        },
        {
          element: <UserProfilePage />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.userProfileDetail,
            sections,
          }),
          path: ROUTE_PATH_LIST.inhabitant,
        },
        {
          element: <ExternalContactProfilePage />,
          loader: getExternalContactProfileGuardLoader({
            availableSections,
            isExternalContactProfileNGAvailable,
            sections,
          }),
          path: ROUTE_PATH_LIST.externalContact,
        },
      ],

      path: ROUTE_PATH_LIST.inhabitants,
    },
    {
      loader: getIntegerParamGuardLoader({
        loader: getProfileLegacyRedirectLoader({
          isExternalContactProfileNGAvailable,
          isUserProfileNGAvailable,
        }),
        paramName: ROUTE_PARAM_INHABITANT_ID,
      }),
      path: ROUTE_PATH_LIST_LEGACY.profile,
    },
    {
      children: [
        {
          element: <Flats />,
          index: true,
        },
        {
          element: <FlatProfile />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.flat,
            sections,
          }),
          path: ROUTE_PATH_LIST.flatProfile,
        },
      ],
      loader: getPermissionGuardLoader({
        sectionName: ROUTE_UI_SECTION_NAME.flats,
        sections,
      }),
      path: ROUTE_PATH_LIST.flats,
    },
    {
      children: [
        {
          element: <Buildings />,
          index: true,
        },
        {
          element: <BuildingProfile />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.buildingDetail,
            sections,
          }),
          path: ROUTE_PATH_LIST.buildingProfile,
        },
      ],
      loader: getPermissionGuardLoader({
        sectionName: ROUTE_UI_SECTION_NAME.buildings,
        sections,
      }),
      path: ROUTE_PATH_LIST.buildings,
    },
    {
      loader: getIntegerParamGuardLoader({
        loader: getBuildingLegacyRedirectLoader({ availableSections }),
        paramName: ROUTE_PARAM_BUILDING_ID,
      }),
      path: ROUTE_PATH_LIST_LEGACY.building,
    },
    {
      children: [
        {
          element: <BuildingGroups />,
          index: true,
        },
        {
          element: <BuildingGroupProfile />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.buildingGroupProfileDetail,
            sections,
          }),
          path: ROUTE_PATH_LIST.buildingGroupProfile,
        },
      ],
      loader: getPermissionGuardLoader({
        sectionName: ROUTE_UI_SECTION_NAME.buildingGroups,
        sections,
      }),
      path: ROUTE_PATH_LIST.buildingsGroup,
    },
    {
      loader: getIntegerParamGuardLoader({
        loader: getBuildingGroupLegacyRedirectLoader({ availableSections }),
        paramName: ROUTE_PARAM_BUILDING_GROUP_ID,
      }),
      path: ROUTE_PATH_LIST_LEGACY.buildingGroup,
    },
    {
      children: [
        {
          index: true,
          loader: ({ request }) =>
            redirect(
              `/${ROUTE_PATH_LIST.buildingsGroup}${new URL(request.url).search}`
            ),
        },
        {
          loader: ({ params, request }) =>
            redirect(
              `${PROFILE_ROUTE_NAMES.buildingGroupProfileNG({
                additionalParams: params.tab ? `/${params.tab}` : EMPTY_STRING,
                id: params.buildingGroupId,
              })}${new URL(request.url).search}`
            ),
          path: ROUTE_PATH_LIST_LEGACY.buildingGroupProfile,
        },
      ],
      path: ROUTE_PATH_LIST_LEGACY.buildingsGroup,
    },
    {
      element: <Tasks />,
      loader: getPermissionGuardLoader({
        sectionName: ROUTE_UI_SECTION_NAME.activities,
        sections,
      }),
      path: ROUTE_PATH_LIST.tasks,
    },
    {
      element: <CalendarPage />,
      loader: () =>
        getPermissionProtectedRouteLoader({ hasAccess: hasCalendar }),
      path: ROUTE_PATH_LIST.calendar,
    },
    {
      children: [
        {
          element: <Chats />,
          index: true,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.chats,
            sections,
          }),
        },
        {
          element: <ChatProfile />,
          loader: getUuidParamGuardLoader({
            loader: getPermissionGuardLoader({
              sectionName: ROUTE_UI_SECTION_NAME.chat,
              sections,
            }),
            paramName: ROUTE_PARAM_CHATROOM_UUID,
          }),
          path: ROUTE_PATH_LIST.chat,
        },
      ],
      element: <Outlet />,
      path: ROUTE_PATH_LIST.chats,
    },
    {
      loader: getUuidParamGuardLoader({
        loader: getChatLegacyRedirectLoader({ availableSections }),
        paramName: ROUTE_PARAM_CHATROOM_UUID,
      }),
      path: ROUTE_PATH_LIST_LEGACY.chat,
    },
    {
      children: [
        {
          element: <DocumentsIndexPage />,
          index: true,
        },
        {
          element: <Documents />,
          path: 'dir/:dirId',
        },
      ],
      element: <Outlet />,
      loader: getPermissionGuardLoader({
        sectionName: ROUTE_UI_SECTION_NAME.documents,
        sections,
      }),
      path: ROUTE_PATH_LIST.files,
    },
    {
      children: [
        {
          element: <DocumentsIndexPage />,
          index: true,
        },
        {
          element: <Documents />,
          path: 'dir/:dirId',
        },
      ],
      element: <Outlet />,
      loader: getPermissionGuardLoader({
        sectionName: ROUTE_UI_SECTION_NAME.documentsDweller,
        sections,
      }),
      path: ROUTE_PATH_LIST.documentsDweller,
    },
    {
      children: [
        {
          element: <DocFlows />,
          index: true,
        },
        { element: <DocFlow />, path: ROUTE_PATH_LIST.docFlow },
      ],
      loader: getPermissionGuardLoader({
        sectionName: ROUTE_UI_SECTION_NAME.docFlows,
        sections,
      }),
      path: ROUTE_PATH_LIST.docFlows,
    },
    {
      children: [
        {
          element: <Providers />,
          index: true,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.providers,
            sections,
          }),
        },
        {
          element: <ProviderProfilePage />,
          loader: getProviderProfileGuardLoader({
            isProviderProfileNGAvailable,
            sections,
          }),
          path: ROUTE_PATH_LIST.provider,
        },
      ],
      path: ROUTE_PATH_LIST.providers,
    },
    {
      children: [
        { element: <RentalPortalIndexRedirect />, index: true },
        {
          element: <RentalPortalOverview />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.overview,
            sections: rentalPortalSections,
          }),
          path: ROUTE_PATH_LIST_RENTAL_PORTAL.overview,
        },
        {
          element: (
            <RentalPortalListings
              ActiveItemPreview={RentalPortalListingProfilePreview}
            />
          ),
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.listings,
            sections: rentalPortalSections,
          }),
          path: ROUTE_PATH_LIST_RENTAL_PORTAL.listings,
        },
        {
          element: <RentalPortalListingProfile />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.listings,
            sections,
          }),
          path: ROUTE_PATH_LIST_RENTAL_PORTAL.listing,
        },
        {
          element: <RentalPortalApplicants />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.applicants,
            sections: rentalPortalSections,
          }),
          path: ROUTE_PATH_LIST_RENTAL_PORTAL.applicants,
        },
        {
          element: <RentalPortalApplicantProfile />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.applicants,
            sections: rentalPortalSections,
          }),
          path: ROUTE_PATH_LIST_RENTAL_PORTAL.applicant,
        },
        {
          element: <RentalPortalSubsectionPage />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.viewings,
            sections: rentalPortalSections,
          }),
          path: ROUTE_PATH_LIST_RENTAL_PORTAL.viewings,
        },
        {
          element: <RentalPortalSubsectionPage />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.contracts,
            sections: rentalPortalSections,
          }),
          path: ROUTE_PATH_LIST_RENTAL_PORTAL.contracts,
        },
        {
          element: <RentalPortalArchive />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.archive,
            sections: rentalPortalSections,
          }),
          path: ROUTE_PATH_LIST_RENTAL_PORTAL.archive,
        },
        {
          element: <MatchSettings />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.settingsMatching,
            sections: rentalPortalSections,
          }),
          path: ROUTE_PATH_LIST_RENTAL_PORTAL.settings,
        },
        {
          element: <RentalPortalIntegrations />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.root,
            sections: rentalPortalSections,
          }),
          path: ROUTE_PATH_LIST_RENTAL_PORTAL.settingsIntegrations,
        },
        {
          element: <IntegrationsImportLog />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.root,
            sections: rentalPortalSections,
          }),
          path: ROUTE_PATH_LIST_RENTAL_PORTAL.settingsImportLog,
        },
        {
          element: <IntegrationsImportLogRun />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.root,
            sections: rentalPortalSections,
          }),
          path: ROUTE_PATH_LIST_RENTAL_PORTAL.settingsImportRun,
        },
        {
          element: <RentalPortalRulesPolicy />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.root,
            sections: rentalPortalSections,
          }),
          path: ROUTE_PATH_LIST_RENTAL_PORTAL.settingsRulesPolicy,
        },
        {
          element: <RentalPortalRightsRoles />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.root,
            sections: rentalPortalSections,
          }),
          path: ROUTE_PATH_LIST_RENTAL_PORTAL.settingsRightsRoles,
        },
        {
          element: <MatchCriterionPage />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.settingsMatching,
            sections: rentalPortalSections,
          }),
          path: ROUTE_PATH_LIST_RENTAL_PORTAL.settingsCriterion,
        },
      ],
      loader: getPermissionGuardLoader({
        sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.root,
        sections: rentalPortalSections,
      }),
      path: ROUTE_PATH_LIST_RENTAL_PORTAL.root,
    },
    {
      element: <FAQs />,
      loader: getPermissionGuardLoader({
        sectionName: ROUTE_UI_SECTION_NAME.faq,
        sections,
      }),
      path: ROUTE_PATH_LIST.faq,
    },
    {
      element: <AboutUs />,
      loader: getPermissionGuardLoader({
        sectionName: ROUTE_UI_SECTION_NAME.aboutUs,
        sections,
      }),
      path: ROUTE_PATH_LIST.aboutUs,
    },
  ]
}

function getPermissionProtectedRoutes({
  availableSections,
  hasCalendar,
  isExternalContactProfileNGAvailable,
  isProviderProfileNGAvailable,
  isUserProfileNGAvailable,
  sections,
}) {
  return {
    children: getGuardedChildren({
      availableSections,
      hasCalendar,
      isExternalContactProfileNGAvailable,
      isProviderProfileNGAvailable,
      isUserProfileNGAvailable,
      sections,
    }),
    errorElement: <ErrorPageNotFound />,
    id: 'protected',
  }
}

// Without the dashboard the newsboard keeps the root, its own path only leads
// back there, and /dashboard is a hidden preview for those allowed to see it.
function getHomeRoutes({
  authorized,
  availableSections,
  hasDashboard,
  sections,
}) {
  const newsboardRoute = {
    element: <Posts />,
    errorElement: <ErrorPage status={APP_STATUS_ERROR} />,
    loader: loaderProps =>
      getAvailableSectionWithRedirectToWebappLoader({
        ...loaderProps,
        hasAccess:
          !authorized || availableSections[ROUTE_UI_SECTION_NAME.newsboard],
      }),
  }

  if (!hasDashboard) {
    return [
      { ...newsboardRoute, index: true },
      { element: <RouteRedirectHome />, path: ROUTE_PATH_LIST.newsboard },
      {
        element: <DashboardBirdview />,
        errorElement: <RouteErrorPage />,
        loader: getPermissionGuardLoader({
          sectionName: ROUTE_UI_SECTION_NAME.dashboard,
          sections,
        }),
        path: ROUTE_PATH_LIST.dashboard,
      },
    ]
  }

  return [
    {
      element: <DashboardBirdview />,
      errorElement: <ErrorPage status={APP_STATUS_ERROR} />,
      index: true,
    },
    { ...newsboardRoute, path: ROUTE_PATH_LIST.newsboard },
    { element: <RouteRedirectHome />, path: ROUTE_PATH_LIST.dashboard },
  ]
}

export function getLayoutRoutes({
  authorized,
  availableSections,
  hasCalendar,
  hasDashboard,
  isExternalContactProfileNGAvailable,
  isProviderProfileNGAvailable,
  isUserProfileNGAvailable,
  sections,
}) {
  return [
    ...getHomeRoutes({
      authorized,
      availableSections,
      hasDashboard,
      sections,
    }),
    getPermissionProtectedRoutes({
      availableSections,
      hasCalendar,
      isExternalContactProfileNGAvailable,
      isProviderProfileNGAvailable,
      isUserProfileNGAvailable,
      sections,
    }),
    { element: <ErrorPageNotFound />, path: '*' },
  ]
}
